import axios from 'axios';

/**
 * Gemeinsame Normalisierungs-Helfer fuer die REST-Antworten des
 * MVGen-Management-Backends.
 *
 * Hintergrund: Die Services reichen Antworten NIE ungeprueft als DTO
 * durch. Eine unvollstaendige Antwort (fehlendes Feld, null statt String,
 * String statt Zahl) wuerde sonst erst tief in der Darstellung auffallen —
 * im schlimmsten Fall als Render-Crash. Diese Helfer buendeln die
 * wiederkehrenden Pruefungen, damit sie nicht je Service erneut
 * entstehen (statisticsService, userService, contractService).
 */

/**
 * true, wenn der Wert ein Objekt ist (null, Primitive UND Arrays fallen
 * raus). Arrays sind bewusst ausgeschlossen: Sie sind zwar `typeof
 * 'object'`, aber niemals ein DTO — ein Array wuerde sonst als "Objekt
 * ohne die erwarteten Felder" durchgehen und still zu Default-Werten
 * normalisiert.
 */
export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Endliche Zahl oder Fallback (fehlende/kaputte Kennzahl bricht nichts). */
export function normalizeCount(value: unknown, fallback = 0): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

/**
 * Nicht negativer Ganzzahl-Index oder Fallback (Seitenzahlen, Groessen).
 * Nachkommastellen werden abgeschnitten, negative Werte verworfen.
 */
export function normalizeIndex(value: unknown, fallback: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
    return fallback;
  }
  return Math.trunc(value);
}

/** String oder Fallback (fuer Pflichtfelder mit sinnvollem Ersatzwert). */
export function normalizeString(value: unknown, fallback: string): string {
  return typeof value === 'string' ? value : fallback;
}

/**
 * String oder null fuer optionale Felder. Leere Strings werden bewusst zu
 * null normalisiert: Die Anzeige unterscheidet nur "vorhanden" und "nicht
 * vorhanden" und wuerde sonst eine leere Zeile statt eines
 * Gedankenstrichs rendern.
 */
export function normalizeNullableString(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null;
}

/**
 * Ganzzahl oder null fuer optionale numerische Felder (z. B.
 * webSearchMaxResults). Nicht-ganzzahlige oder nicht-numerische Werte
 * werden zu null normalisiert statt einen kaputten Wert durchzureichen.
 */
export function normalizeNullableInteger(value: unknown): number | null {
  return typeof value === 'number' && Number.isInteger(value) ? value : null;
}

/** Boolean oder Fallback (unbekannte Werte gelten als false). */
export function normalizeBoolean(value: unknown, fallback = false): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

/**
 * Liest die `message` eines Backend-Fehlerbodys (ErrorResponseDTO) aus
 * einem beliebigen Fehler. Liefert die deutsche `fallback`-Meldung, wenn
 * der Fehler kein Axios-Fehler ist, der Body kein Objekt mit einer
 * nicht-leeren `message`-Zeichenkette enthaelt, oder die Backend-Meldung
 * generisch/leer ist.
 *
 * Wird von den Services genutzt, damit Hooks nicht selbst gegen axios
 * pruefen muessen (analog isNotFoundError in userService).
 */
export function extractBackendErrorMessage(error: unknown, fallback: string): string {
  if (!axios.isAxiosError(error)) {
    return fallback;
  }
  const data: unknown = error.response?.data;
  if (!isRecord(data)) {
    return fallback;
  }
  const message = data.message;
  return typeof message === 'string' && message.trim() !== '' ? message : fallback;
}
